import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateTimePickerModule } from '@progress/kendo-angular-dateinputs';
import { ForcePublishService } from './force-publish.service';
import { DropDownListModel } from '../models/dropdown-list-model';
import { ForcePublishReceiversAPIService } from '../force-publish-receivers/force-publish-receivers.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-force-publish',
  standalone: true,
  imports: [CommonModule, FormsModule, DateTimePickerModule],
  providers: [ForcePublishReceiversAPIService],
  templateUrl: './force-publish.component.html'
})
export class ForcePublishComponent implements OnInit {
  public readonly isProduction = environment.production;
  public alsisProductCodesInput: string = '';
  public itemIdsInput: string = '';
  public suppliersInput: string = '';
  public receivers: string[] = [];
  public selectedReceiver: string = '';
  public itemStateDropdownOptions: DropDownListModel[] = [
    { value: '10', text: 'NEW' },
    { value: '20', text: 'ACTIVE' },
    { value: '30', text: 'INACTIVE' },
    { value: '40', text: 'DELETED' }
  ];
  public selectedItemState: string = '';
  public isReplaced: boolean | null = null;
  public isConfigured: boolean | null = null;
  public sendToLegacy: boolean = false;
  public lastModificationDateTimeFrom: Date | null = null;
  public lastModificationDateTimeTo: Date | null = null;
  public showSpinner: boolean = false;
  public error: boolean = false;
  public errorMessage: string = '';
  public success: boolean = false;
  public successMessage: string = '';

  constructor(private forcePublishService: ForcePublishService, private forcePublishReceiversAPIService: ForcePublishReceiversAPIService) { }

  ngOnInit(): void {
    this.forcePublishReceiversAPIService.getAll().subscribe({
      next: (result) => {
        this.receivers = result.map(r => r.receiver);
      },
      error: () => {
        this.receivers = [];
      }
    });
  }

  get itemIdsCount(): number {
    if (!this.itemIdsInput || this.itemIdsInput.trim() === '') {
      return 0;
    }
    return this.itemIdsInput.split(',').map(id => id.trim()).filter(id => id !== '').length;
  }

  get isItemIdsLimitExceeded(): boolean {
    return this.itemIdsCount > 50;
  }

  onSendToLegacyChange(sendToLegacy: boolean): void {
    this.sendToLegacy = sendToLegacy;
    if (!sendToLegacy) {
      return;
    }

    this.alsisProductCodesInput = '';
    this.suppliersInput = '';
    this.selectedReceiver = 'internal';
    this.selectedItemState = '';
    this.isReplaced = null;
    this.isConfigured = null;
    this.lastModificationDateTimeFrom = null;
    this.lastModificationDateTimeTo = null;
  }

  onSubmit(): void {
    // Reset messages
    this.error = false;
    this.errorMessage = '';
    this.success = false;
    this.successMessage = '';

    // Validate receiver
    if (!this.sendToLegacy && !this.selectedReceiver) {
      this.error = true;
      this.errorMessage = 'Please select a receiver';
      return;
    }

    // Validate ItemIds limit
    if (this.isItemIdsLimitExceeded) {
      this.error = true;
      this.errorMessage = 'Cannot submit more than 50 Item IDs';
      return;
    }

    // Parse comma-separated values and trim whitespace
    const alsisProductCodes = this.alsisProductCodesInput
      ? this.alsisProductCodesInput.split(',').map(code => code.trim()).filter(code => code !== '')
      : [];

    // Parse ItemIds if provided
    const itemIds = this.itemIdsInput
      ? this.itemIdsInput.split(',').map(id => id.trim()).filter(id => id !== '')
      : [];

    // Parse suppliers if provided
    const suppliers = this.suppliersInput
      ? this.suppliersInput.split(',').map(s => s.trim()).filter(s => s !== '')
      : [];

    // Call the service
    this.showSpinner = true;
    this.forcePublishService.forcePublish(alsisProductCodes, this.selectedReceiver, this.selectedItemState, this.isReplaced, this.isConfigured, suppliers, itemIds, this.lastModificationDateTimeFrom, this.lastModificationDateTimeTo, this.sendToLegacy).subscribe({
      next: (response) => {
        this.showSpinner = false;
        this.success = true;
        
        // Build detailed success message
        const details: string[] = [];
        if (alsisProductCodes.length > 0) {
          details.push(`${alsisProductCodes.length} Alsis Product Code(s)`);
        }
        if (itemIds.length > 0) {
          details.push(`${itemIds.length} Item ID(s)`);
        }
        if (suppliers.length > 0) {
          details.push(`${suppliers.length} Supplier(s)`);
        }
        if (this.selectedItemState) {
          const stateText = this.itemStateDropdownOptions.find(s => s.value === this.selectedItemState)?.text || this.selectedItemState;
          details.push(`Item State: ${stateText}`);
        }
        if (this.isReplaced !== null) {
          details.push(`Is Replaced: ${this.isReplaced}`);
        }
        if (this.isConfigured !== null) {
          details.push(`Is Configured: ${this.isConfigured}`);
        }
        
        this.successMessage = `Successfully submitted force publish with: ${details.join(', ')}`;
      },
      error: (error) => {
        this.showSpinner = false;
        this.error = true;
        this.errorMessage = error.error?.message || error.message || 'An error occurred while submitting the request';
      }
    });
  }

}
